import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import DailyBriefingBar from '../components/DailyBriefingBar';
import {
  ArrowLeft, AlertTriangle, CheckCircle2, Clock, Calendar, Users,
  CreditCard, Home, Baby, TrendingUp, RefreshCw, ChevronRight,
  ClipboardList, CalendarDays, FileText, Globe
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLATFORM_COLORS = {
  instagram: '#E1306C', tiktok: '#000000', linkedin: '#0A66C2',
  youtube: '#FF0000', twitter: '#1DA1F2', facebook: '#1877F2',
  website: '#D4AF37', email: '#8B7355', other: '#6B7280'
};

const WeeklyBriefing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBriefing();
  }, []);

  const fetchBriefing = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/briefing/weekly`, { withCredentials: true });
      setBriefing(res.data);
    } catch (error) {
      console.error('Error fetching briefing:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
    } catch { return dateStr; }
  };

  const getWeekRange = () => {
    if (!briefing?.week_of) return '';
    const monday = new Date(briefing.week_of);
    const sunday = new Date(monday);
    sunday.setDate(sunday.getDate() + 6);
    return `${monday.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} — ${sunday.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAF9F7' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: '#D4AF37' }}></div>
          <p className="mt-4" style={{ color: '#8B7355' }}>Preparing your briefing...</p>
        </div>
      </div>
    );
  }

  if (!briefing) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAF9F7' }}>
        <p style={{ color: '#8B7355' }}>Could not load briefing data.</p>
      </div>
    );
  }

  const totalOverdue = briefing.overdue.total;
  const totalThisWeek = briefing.this_week.tasks.length + briefing.this_week.content.length + briefing.this_week.deadlines.length;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAF9F7' }} data-testid="weekly-briefing-page">
      <DailyBriefingBar />

      {/* Header */}
      <header style={{ backgroundColor: '#2C2416' }}>
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-8">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 mb-6 transition-all duration-200 opacity-60 hover:opacity-100" style={{ color: '#D4AF37' }} data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4" /><span className="text-sm">Back to Dashboard</span>
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-light tracking-tight text-white mb-1">Weekly Briefing</h1>
              <p className="text-lg" style={{ color: '#D4AF37' }}>{getWeekRange()}</p>
            </div>
            <button onClick={fetchBriefing} className="p-3 rounded-full transition-all hover:bg-white/10" data-testid="refresh-briefing">
              <RefreshCw className="w-5 h-5" style={{ color: '#D4AF37' }} />
            </button>
          </div>

          {/* Quick Stats Strip */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mt-8">
            <div className="text-center p-3 rounded-lg" style={{ backgroundColor: totalOverdue > 0 ? 'rgba(220,38,38,0.15)' : 'rgba(255,255,255,0.08)' }}>
              <div className="text-2xl font-light" style={{ color: totalOverdue > 0 ? '#FCA5A5' : '#D4AF37' }}>{totalOverdue}</div>
              <div className="text-xs uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.5)' }}>Overdue</div>
            </div>
            <div className="text-center p-3 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}>
              <div className="text-2xl font-light" style={{ color: '#D4AF37' }}>{totalThisWeek}</div>
              <div className="text-xs uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.5)' }}>This Week</div>
            </div>
            <div className="text-center p-3 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}>
              <div className="text-2xl font-light text-white">{briefing.this_week.done_tasks.length}</div>
              <div className="text-xs uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.5)' }}>Done</div>
            </div>
            <div className="text-center p-3 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}>
              <div className="text-2xl font-light text-white">{briefing.personal.urgent.length + briefing.personal.other.length}</div>
              <div className="text-xs uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.5)' }}>Personal</div>
            </div>
            <div className="text-center p-3 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}>
              <div className="text-2xl font-light text-white">{Object.keys(briefing.team_workload).length}</div>
              <div className="text-xs uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.5)' }}>People</div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 lg:px-8 py-8 space-y-8">

        {/* WEEKLY SCHEDULE */}
        <section data-testid="weekly-schedule-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#D4AF37' + '20' }}>
              <Clock className="w-4 h-4" style={{ color: '#D4AF37' }} />
            </div>
            <h2 className="text-xl font-medium" style={{ color: '#2C2416' }}>Your Week</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {[
              { day: 'Mon', theme: 'Finance & Admin', color: '#1E3A8A', sub: 'HMRC, invoices, tax' },
              { day: 'Tue', theme: 'Deep Work 1', color: '#166534', sub: 'Build, processes, MAPS' },
              { day: 'Wed', theme: 'Deep Work 2', color: '#166534', sub: 'Continue momentum' },
              { day: 'Thu', theme: 'Sales & Meetings', color: '#C2410C', sub: 'Outreach, calls, grocery order' },
              { day: 'Fri', theme: 'Content & Wrap-Up', color: '#D4AF37', sub: 'Create, schedule, tidy up' },
              { day: 'Sat', theme: 'Family & Home', color: '#8B7355', sub: 'Activities, bath, shopping' },
              { day: 'Sun', theme: 'Church & Rest', color: '#8B7355', sub: 'Family, recharge' },
            ].map((d, i) => {
              const isToday = new Date().getDay() === (i === 6 ? 0 : i + 1);
              return (
                <div key={d.day} className="p-3 rounded-sm border transition-all" style={{
                  borderColor: isToday ? d.color : '#E5E1DB',
                  borderWidth: isToday ? '2px' : '1px',
                  backgroundColor: isToday ? d.color + '08' : 'white'
                }}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold uppercase" style={{ color: d.color }}>{d.day}</span>
                    {isToday && <span className="text-xs px-1.5 py-0.5 rounded-full text-white" style={{ backgroundColor: d.color }}>Today</span>}
                  </div>
                  <p className="text-sm font-medium" style={{ color: '#2C2416' }}>{d.theme}</p>
                  <p className="text-xs" style={{ color: '#8B7355' }}>{d.sub}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* OVERDUE — Red Alert Section */}
        {totalOverdue > 0 && (
          <section data-testid="overdue-section">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#FEF2F2' }}>
                <AlertTriangle className="w-4 h-4" style={{ color: '#DC2626' }} />
              </div>
              <h2 className="text-xl font-medium" style={{ color: '#991B1B' }}>Overdue</h2>
              <span className="text-xs px-2.5 py-1 rounded-full font-medium" style={{ backgroundColor: '#FEF2F2', color: '#991B1B' }}>{totalOverdue}</span>
            </div>
            <div className="space-y-2">
              {briefing.overdue.tasks.map(t => (
                <div key={t.id} className="flex items-center gap-4 p-4 bg-white border-l-4 rounded-sm" style={{ borderLeftColor: '#DC2626', borderColor: '#FECACA' }} data-testid={`overdue-task-${t.id}`}>
                  <ClipboardList className="w-4 h-4 flex-shrink-0" style={{ color: '#DC2626' }} />
                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-sm" style={{ color: '#2C2416' }}>{t.title}</span>
                    <div className="flex items-center gap-3 mt-0.5 text-xs" style={{ color: '#8B7355' }}>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: t.company_color }} />{t.company_name}</span>
                      {t.due_date && <span>Due {formatDate(t.due_date)}</span>}
                      {t.assigned_to && <span>Assigned: {t.assigned_to}</span>}
                    </div>
                  </div>
                  <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: '#FEF2F2', color: '#991B1B' }}>Task</span>
                </div>
              ))}
              {briefing.overdue.content.map(c => (
                <div key={c.id} className="flex items-center gap-4 p-4 bg-white border-l-4 rounded-sm" style={{ borderLeftColor: '#DC2626', borderColor: '#FECACA' }} data-testid={`overdue-content-${c.id}`}>
                  <CalendarDays className="w-4 h-4 flex-shrink-0" style={{ color: '#DC2626' }} />
                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-sm" style={{ color: '#2C2416' }}>{c.title}</span>
                    <div className="flex items-center gap-3 mt-0.5 text-xs" style={{ color: '#8B7355' }}>
                      <span>{c.company_name}</span>
                      <span style={{ color: PLATFORM_COLORS[c.platform] || '#8B7355' }}>{c.platform}</span>
                      {c.assigned_to && <span>Assigned: {c.assigned_to}</span>}
                    </div>
                  </div>
                  <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: '#FEF2F2', color: '#991B1B' }}>Content</span>
                </div>
              ))}
              {briefing.overdue.deadlines.map(d => (
                <div key={d.id} className="flex items-center gap-4 p-4 bg-white border-l-4 rounded-sm" style={{ borderLeftColor: '#DC2626', borderColor: '#FECACA' }} data-testid={`overdue-deadline-${d.id}`}>
                  <Calendar className="w-4 h-4 flex-shrink-0" style={{ color: '#DC2626' }} />
                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-sm" style={{ color: '#2C2416' }}>{d.title}</span>
                    <div className="flex items-center gap-3 mt-0.5 text-xs" style={{ color: '#8B7355' }}>
                      <span>{d.company_name}</span>
                      <span>Due {formatDate(d.date)}</span>
                      <span>{d.deadline_type}</span>
                    </div>
                  </div>
                  <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: '#FEF2F2', color: '#991B1B' }}>Deadline</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* THIS WEEK */}
        <section data-testid="this-week-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#D4AF37' + '20' }}>
              <Calendar className="w-4 h-4" style={{ color: '#D4AF37' }} />
            </div>
            <h2 className="text-xl font-medium" style={{ color: '#2C2416' }}>This Week</h2>
            <span className="text-xs px-2.5 py-1 rounded-full" style={{ backgroundColor: '#F5F1EB', color: '#8B7355' }}>{totalThisWeek} items</span>
          </div>

          {/* Tasks This Week */}
          {briefing.this_week.tasks.length > 0 && (
            <div className="mb-4">
              <h3 className="text-xs uppercase tracking-widest mb-2 flex items-center gap-2" style={{ color: '#8B7355' }}>
                <ClipboardList className="w-3.5 h-3.5" /> Tasks ({briefing.this_week.tasks.length})
              </h3>
              <div className="space-y-2">
                {briefing.this_week.tasks.map(t => (
                  <div key={t.id} className="flex items-center gap-4 p-4 bg-white border rounded-sm transition-all hover:shadow-sm" style={{ borderColor: '#E5E1DB', borderLeftWidth: '4px', borderLeftColor: t.company_color }} data-testid={`week-task-${t.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm" style={{ color: '#2C2416' }}>{t.title}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: t.priority === 'high' ? '#FEF2F2' : t.priority === 'low' ? '#F0FDF4' : '#FFFBEB', color: t.priority === 'high' ? '#991B1B' : t.priority === 'low' ? '#166534' : '#92400E' }}>{t.priority}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs" style={{ color: '#8B7355' }}>
                        <span>{t.company_name}</span>
                        {t.due_date && <span>{formatDate(t.due_date)}</span>}
                        {t.assigned_to && <span>Assigned: <strong>{t.assigned_to}</strong></span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Content This Week */}
          {briefing.this_week.content.length > 0 && (
            <div className="mb-4">
              <h3 className="text-xs uppercase tracking-widest mb-2 flex items-center gap-2" style={{ color: '#8B7355' }}>
                <CalendarDays className="w-3.5 h-3.5" /> Content ({briefing.this_week.content.length})
              </h3>
              <div className="space-y-2">
                {briefing.this_week.content.map(c => (
                  <div key={c.id} className="flex items-center gap-4 p-4 bg-white border rounded-sm transition-all hover:shadow-sm" style={{ borderColor: '#E5E1DB', borderLeftWidth: '4px', borderLeftColor: PLATFORM_COLORS[c.platform] || '#8B7355' }} data-testid={`week-content-${c.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm" style={{ color: '#2C2416' }}>{c.title}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: (PLATFORM_COLORS[c.platform] || '#8B7355') + '15', color: PLATFORM_COLORS[c.platform] || '#8B7355' }}>{c.platform}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full border" style={{ borderColor: '#E5E1DB', color: '#8B7355' }}>{c.content_type}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs" style={{ color: '#8B7355' }}>
                        <span>{c.company_name}</span>
                        {c.scheduled_date && <span>{formatDate(c.scheduled_date)}</span>}
                        {c.assigned_to && <span>Assigned: <strong>{c.assigned_to}</strong></span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Deadlines This Week */}
          {briefing.this_week.deadlines.length > 0 && (
            <div className="mb-4">
              <h3 className="text-xs uppercase tracking-widest mb-2 flex items-center gap-2" style={{ color: '#8B7355' }}>
                <Calendar className="w-3.5 h-3.5" /> Deadlines ({briefing.this_week.deadlines.length})
              </h3>
              <div className="space-y-2">
                {briefing.this_week.deadlines.map(d => (
                  <div key={d.id} className="flex items-center gap-4 p-4 bg-white border rounded-sm" style={{ borderColor: '#E5E1DB', borderLeftWidth: '4px', borderLeftColor: d.company_color }} data-testid={`week-deadline-${d.id}`}>
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-sm" style={{ color: '#2C2416' }}>{d.title}</span>
                      <div className="flex items-center gap-3 mt-1 text-xs" style={{ color: '#8B7355' }}>
                        <span>{d.company_name}</span>
                        <span>{formatDate(d.date)}</span>
                        <span>{d.deadline_type}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {totalThisWeek === 0 && (
            <div className="bg-white border rounded-sm p-8 text-center" style={{ borderColor: '#E5E1DB' }}>
              <CheckCircle2 className="w-12 h-12 mx-auto mb-3" style={{ color: '#16A34A' }} />
              <p className="font-medium" style={{ color: '#2C2416' }}>Clear week ahead!</p>
              <p className="text-sm mt-1" style={{ color: '#8B7355' }}>No tasks, content, or deadlines scheduled for this week.</p>
            </div>
          )}

          {/* Done This Week */}
          {briefing.this_week.done_tasks.length > 0 && (
            <div className="mt-4">
              <h3 className="text-xs uppercase tracking-widest mb-2 flex items-center gap-2" style={{ color: '#16A34A' }}>
                <CheckCircle2 className="w-3.5 h-3.5" /> Completed This Week ({briefing.this_week.done_tasks.length})
              </h3>
              <div className="space-y-1">
                {briefing.this_week.done_tasks.map(t => (
                  <div key={t.id} className="flex items-center gap-3 p-3 bg-white border rounded-sm opacity-60" style={{ borderColor: '#E5E1DB' }}>
                    <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: '#16A34A' }} />
                    <span className="text-sm line-through" style={{ color: '#2C2416' }}>{t.title}</span>
                    <span className="text-xs" style={{ color: '#8B7355' }}>{t.company_name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* PERSONAL */}
        {(briefing.personal.urgent.length > 0 || briefing.personal.other.length > 0) && (
          <section data-testid="personal-section">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#C2410C20' }}>
                <Home className="w-4 h-4" style={{ color: '#C2410C' }} />
              </div>
              <h2 className="text-xl font-medium" style={{ color: '#2C2416' }}>Personal & Family</h2>
            </div>
            <div className="space-y-2">
              {briefing.personal.urgent.map(p => (
                <div key={p.id} className="flex items-center gap-4 p-4 bg-white border rounded-sm" style={{ borderColor: '#E5E1DB', borderLeftWidth: '4px', borderLeftColor: '#C2410C' }} data-testid={`personal-urgent-${p.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm" style={{ color: '#2C2416' }}>{p.title}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: '#FEF2F2', color: '#991B1B' }}>This Week</span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs" style={{ color: '#8B7355' }}>
                      <span className="capitalize">{p.category}</span>
                      {p.subcategory && <span className="capitalize">{p.subcategory}</span>}
                      {p.date && <span>{formatDate(p.date)}</span>}
                    </div>
                    {p.notes && <p className="text-xs mt-1" style={{ color: '#8B7355' }}>{p.notes}</p>}
                  </div>
                </div>
              ))}
              {briefing.personal.other.map(p => (
                <div key={p.id} className="flex items-center gap-4 p-4 bg-white border rounded-sm" style={{ borderColor: '#E5E1DB', borderLeftWidth: '4px', borderLeftColor: '#E5E1DB' }} data-testid={`personal-other-${p.id}`}>
                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-sm" style={{ color: '#2C2416' }}>{p.title}</span>
                    <div className="flex items-center gap-3 mt-1 text-xs" style={{ color: '#8B7355' }}>
                      <span className="capitalize">{p.category}</span>
                      {p.subcategory && <span className="capitalize">{p.subcategory}</span>}
                      {p.date && <span>{formatDate(p.date)}</span>}
                    </div>
                    {p.notes && <p className="text-xs mt-1" style={{ color: '#8B7355' }}>{p.notes}</p>}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* FINANCIAL */}
        {(briefing.financial.bills_due.length > 0 || briefing.financial.invoices_due.length > 0) && (
          <section data-testid="financial-section">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#D4AF3720' }}>
                <CreditCard className="w-4 h-4" style={{ color: '#D4AF37' }} />
              </div>
              <h2 className="text-xl font-medium" style={{ color: '#2C2416' }}>Financials Due</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {briefing.financial.bills_due.length > 0 && (
                <div className="bg-white border rounded-sm p-5" style={{ borderColor: '#E5E1DB' }}>
                  <h3 className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: '#DC2626' }}>Bills to Pay</h3>
                  {briefing.financial.bills_due.map(b => (
                    <div key={b.id} className="flex justify-between items-center py-2 border-b" style={{ borderColor: '#F5F1EB' }}>
                      <div>
                        <span className="text-sm" style={{ color: '#2C2416' }}>{b.title}</span>
                        <p className="text-xs" style={{ color: '#8B7355' }}>{b.company_name}</p>
                      </div>
                      <span className="font-mono text-sm font-medium" style={{ color: '#DC2626' }}>{b.currency === 'GBP' ? '£' : '$'}{b.amount?.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              )}
              {briefing.financial.invoices_due.length > 0 && (
                <div className="bg-white border rounded-sm p-5" style={{ borderColor: '#E5E1DB' }}>
                  <h3 className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: '#16A34A' }}>Invoices to Chase</h3>
                  {briefing.financial.invoices_due.map(inv => (
                    <div key={inv.id} className="flex justify-between items-center py-2 border-b" style={{ borderColor: '#F5F1EB' }}>
                      <div>
                        <span className="text-sm" style={{ color: '#2C2416' }}>{inv.title}</span>
                        <p className="text-xs" style={{ color: '#8B7355' }}>{inv.company_name}</p>
                      </div>
                      <span className="font-mono text-sm font-medium" style={{ color: '#16A34A' }}>{inv.currency === 'GBP' ? '£' : '$'}{inv.amount?.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        )}

        {/* TEAM WORKLOAD */}
        {Object.keys(briefing.team_workload).length > 0 && (
          <section data-testid="team-section">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#1E3A8A20' }}>
                <Users className="w-4 h-4" style={{ color: '#1E3A8A' }} />
              </div>
              <h2 className="text-xl font-medium" style={{ color: '#2C2416' }}>Team Workload</h2>
            </div>
            <div className="bg-white border rounded-sm overflow-hidden" style={{ borderColor: '#E5E1DB' }}>
              <div className="grid grid-cols-3 px-6 py-3 text-xs uppercase tracking-widest" style={{ backgroundColor: '#FDFCFB', color: '#8B7355', borderBottom: '1px solid #E5E1DB' }}>
                <span>Person</span><span className="text-center">Tasks</span><span className="text-center">Content</span>
              </div>
              {Object.entries(briefing.team_workload).map(([name, counts]) => (
                <div key={name} className="grid grid-cols-3 px-6 py-3 items-center" style={{ borderBottom: '1px solid #F5F1EB' }}>
                  <span className="font-medium text-sm" style={{ color: '#2C2416' }}>{name}</span>
                  <span className="text-center text-sm" style={{ color: counts.tasks > 0 ? '#D4AF37' : '#CBD5E1' }}>{counts.tasks}</span>
                  <span className="text-center text-sm" style={{ color: counts.content > 0 ? '#D4AF37' : '#CBD5E1' }}>{counts.content}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* COMPANY OVERVIEW */}
        <section data-testid="companies-overview">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#D4AF3720' }}>
              <Globe className="w-4 h-4" style={{ color: '#D4AF37' }} />
            </div>
            <h2 className="text-xl font-medium" style={{ color: '#2C2416' }}>Companies At-a-Glance</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {briefing.companies_summary.map(c => (
              <div key={c.name} className="bg-white border rounded-sm p-5" style={{ borderColor: '#E5E1DB', borderTopWidth: '4px', borderTopColor: c.color }} data-testid={`company-overview-${c.name}`}>
                <h3 className="font-medium mb-3" style={{ color: '#2C2416' }}>{c.name}</h3>
                <div className="flex justify-between text-sm">
                  <span style={{ color: '#8B7355' }}>Open Tasks</span>
                  <span className="font-medium" style={{ color: c.open_tasks > 0 ? '#D4AF37' : '#CBD5E1' }}>{c.open_tasks}</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span style={{ color: '#8B7355' }}>Pending Content</span>
                  <span className="font-medium" style={{ color: c.pending_content > 0 ? '#D4AF37' : '#CBD5E1' }}>{c.pending_content}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* UPCOMING RENEWALS */}
        {briefing.renewals.length > 0 && (
          <section data-testid="renewals-section">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#D4AF3720' }}>
                <RefreshCw className="w-4 h-4" style={{ color: '#D4AF37' }} />
              </div>
              <h2 className="text-xl font-medium" style={{ color: '#2C2416' }}>Upcoming Renewals (30 days)</h2>
            </div>
            <div className="bg-white border rounded-sm" style={{ borderColor: '#E5E1DB' }}>
              {briefing.renewals.map(r => (
                <div key={r.id} className="flex items-center justify-between px-6 py-3" style={{ borderBottom: '1px solid #F5F1EB' }}>
                  <div>
                    <span className="text-sm font-medium" style={{ color: '#2C2416' }}>{r.service} — {r.description}</span>
                    <p className="text-xs" style={{ color: '#8B7355' }}>{r.company_name}</p>
                  </div>
                  <div className="text-right">
                    <span className="font-mono text-sm" style={{ color: '#2C2416' }}>{formatDate(r.renewal_date)}</span>
                    {r.cost_monthly > 0 && <p className="text-xs" style={{ color: '#8B7355' }}>{r.currency === 'GBP' ? '£' : '$'}{r.cost_monthly}/mo</p>}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* LOOKING AHEAD */}
        {(briefing.upcoming.tasks.length > 0 || briefing.upcoming.content.length > 0 || briefing.upcoming.deadlines.length > 0) && (
          <section data-testid="upcoming-section">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#F5F1EB' }}>
                <ChevronRight className="w-4 h-4" style={{ color: '#8B7355' }} />
              </div>
              <h2 className="text-xl font-medium" style={{ color: '#2C2416' }}>Looking Ahead</h2>
            </div>
            <div className="bg-white border rounded-sm p-5" style={{ borderColor: '#E5E1DB' }}>
              {briefing.upcoming.tasks.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-xs uppercase tracking-widest mb-2" style={{ color: '#8B7355' }}>Upcoming Tasks</h3>
                  {briefing.upcoming.tasks.slice(0, 5).map(t => (
                    <div key={t.id} className="flex items-center gap-3 py-2" style={{ borderBottom: '1px solid #F5F1EB' }}>
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: t.company_color }} />
                      <span className="text-sm" style={{ color: '#2C2416' }}>{t.title}</span>
                      <span className="text-xs ml-auto" style={{ color: '#8B7355' }}>{t.due_date ? formatDate(t.due_date) : 'No date'}</span>
                    </div>
                  ))}
                </div>
              )}
              {briefing.upcoming.content.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-xs uppercase tracking-widest mb-2" style={{ color: '#8B7355' }}>Upcoming Content</h3>
                  {briefing.upcoming.content.slice(0, 5).map(c => (
                    <div key={c.id} className="flex items-center gap-3 py-2" style={{ borderBottom: '1px solid #F5F1EB' }}>
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: PLATFORM_COLORS[c.platform] || '#8B7355' }} />
                      <span className="text-sm" style={{ color: '#2C2416' }}>{c.title}</span>
                      <span className="text-xs ml-auto" style={{ color: '#8B7355' }}>{formatDate(c.scheduled_date)}</span>
                    </div>
                  ))}
                </div>
              )}
              {briefing.upcoming.deadlines.length > 0 && (
                <div>
                  <h3 className="text-xs uppercase tracking-widest mb-2" style={{ color: '#8B7355' }}>Upcoming Deadlines</h3>
                  {briefing.upcoming.deadlines.slice(0, 5).map(d => (
                    <div key={d.id} className="flex items-center gap-3 py-2" style={{ borderBottom: '1px solid #F5F1EB' }}>
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: d.company_color }} />
                      <span className="text-sm" style={{ color: '#2C2416' }}>{d.title}</span>
                      <span className="text-xs ml-auto" style={{ color: '#8B7355' }}>{formatDate(d.date)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default WeeklyBriefing;
